import type { ArgsOf, RequestOf } from "../../../access/types.ts";
import { Access } from "../../../access/types.ts";

// Shared parameters for both `device` and `ssh` accesses
export const tailscaleDeviceParameters = {
  organization: {
    type: "static",
    label: "Organization",
    description: "The tailnet organization",
    optional: true,
    isPreselect: true,
    waitForInput: true,
  },
  name: {
    // The Tailscale list all devices API doesn't have pagination, which indicates that all devices
    // should be retunrned quickly in one request. However, Slack has a limit of how many items can be
    // shown in static select.
    // TODO @ENG-5741: Use "dynamic" parameter for device lookup
    type: "static",
    label: "Device name",
    // The description is not accurate in Slack because you search what you see (the `value`) in the
    // returned `option` objects. Even if we change to "dynamic", `fuzzySearch` still works on the values.
    // We need to customize `fuzzySearch` to search on fields of the device.
    // TODO @ENG-5741: Use "dynamic" parameter for device lookup
    description:
      "Hostname, IP address, full domain, node ID, or node key of the device",
  },
  hostname: {
    type: "generated",
    label: "Hostname",
    description: "The hostname of the device",
  },
  nodeId: {
    type: "generated",
    label: "Node ID",
    description: "The node ID of the device",
  },
  ipv4: {
    type: "generated",
    label: "IPv4 address",
    optional: true,
    description: "The IPv4 address of the device",
  },
  ipv6: {
    type: "generated",
    label: "IPv6 address",
    optional: true,
    description: "The IPv6 address of the device",
  },
  nodeKey: {
    type: "generated",
    label: "Node key",
    description: "The node key of the device",
  },
  fullDomain: {
    type: "generated",
    label: "Full domain",
    description: "The full domain of the device",
  },
  tags: {
    type: "generated",
    label: "Tags",
    multivalued: true,
    description: "The tags associated with the device",
  },
} as const;

// Parameters for `tag` access type
export const tailscaleTagParameters = {
  organization: {
    type: "static",
    label: "Organization",
    description: "The tailnet organization",
    optional: true,
    isPreselect: true,
    waitForInput: true,
  },
  name: {
    type: "static",
    label: "Tag name",
    description: "The tag name to grant access to",
  },
} as const;

export const TailscaleAccesses = {
  device: Access({
    // Important to distinguish between "network layer" and "application layer" access
    // See https://tailscale.com/kb/1538/grants-syntax#network-layer-capabilities
    description:
      "Add a grant for network layer access to a device in the Tailscale policy file",
    label: "Device",
    maturity: "beta",
    delegation: {},
    generated: {},
    parameters: tailscaleDeviceParameters,
    render: { locator: "nodeId" } as const,
  }),
  tag: Access({
    description:
      "Add a grant for network layer access to all devices with a specific tag in the Tailscale policy file",
    label: "Tag",
    maturity: "beta",
    delegation: {},
    generated: {},
    parameters: tailscaleTagParameters,
    render: { locator: "name" } as const,
  }),
  ssh: Access({
    description:
      "Add SSH access to a device in the Tailscale policy file with both grants and SSH rules",
    label: "SSH",
    maturity: "beta",
    delegation: {},
    generated: {},
    parameters: tailscaleDeviceParameters,
    render: { locator: "nodeId" } as const,
  }),
};

export type TailscaleAccesses = typeof TailscaleAccesses;
export type TailscaleAccess = TailscaleAccesses[keyof TailscaleAccesses];

export type TailscaleDeviceAccess = TailscaleAccesses["device"];
export type TailscaleTagAccess = TailscaleAccesses["tag"];
export type TailscaleSshAccess = TailscaleAccesses["ssh"];

export type TailscaleDeviceRequest = RequestOf<TailscaleDeviceAccess>;
export type TailscaleTagRequest = RequestOf<TailscaleTagAccess>;
export type TailscaleSshRequest = RequestOf<TailscaleSshAccess>;

export type TailscaleDeviceAccessArgs = ArgsOf<TailscaleDeviceAccess>;
export type TailscaleTagAccessArgs = ArgsOf<TailscaleTagAccess>;
